import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Zoom
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CustomStackFullWidth } from '../styled-components/CustomStyles.style';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';
import StoreIcon from '@mui/icons-material/Store';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getTimeUntilLaunch, getFormattedLaunchDate } from '../utils/launchDate';
import CustomImageContainer from './CustomImageContainer';
import DollarSignHighlighter from './DollarSignHighlighter';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';

const ComingSoonContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
    : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: theme.palette.mode === 'dark'
      ? `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.primary.light, 0.3)} 0%, transparent 50%),
         radial-gradient(circle at 80% 20%, ${alpha(theme.palette.primary.customType2, 0.3)} 0%, transparent 50%)`
      : `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%),
         radial-gradient(circle at 80% 20%, ${alpha(theme.palette.primary.customType2, 0.1)} 0%, transparent 50%)`,
    zIndex: 1,
  }
}));

const CountdownCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  textAlign: 'center',
  background: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.9)
    : theme.palette.background.paper,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: '20px',
  boxShadow: theme.palette.mode === 'dark'
    ? `0px 8px 32px ${alpha(theme.palette.common.black, 0.3)}`
    : `0px 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0px 12px 40px ${alpha(theme.palette.common.black, 0.4)}`
      : `0px 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
  }
}));

const MainCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6, 4),
  background: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.95)
    : theme.palette.background.paper,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: '32px',
  boxShadow: theme.palette.mode === 'dark'
    ? `0px 20px 60px ${alpha(theme.palette.common.black, 0.4)}`
    : `0px 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
  position: 'relative',
  zIndex: 2,
  maxWidth: '900px',
  width: '100%',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(4, 2),
    margin: theme.spacing(2),
  }
}));

const CTACard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  background: theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.paper, 0.9)
    : theme.palette.background.paper,
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: '20px',
  boxShadow: theme.palette.mode === 'dark'
    ? `0px 8px 32px ${alpha(theme.palette.common.black, 0.3)}`
    : `0px 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.palette.mode === 'dark'
      ? `0px 12px 40px ${alpha(theme.palette.common.black, 0.4)}`
      : `0px 12px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
    borderColor: theme.palette.primary.main,
  }
}));

const CTAButton = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.customType2})`,
  color: theme.palette.whiteContainer.main,
  borderRadius: '12px',
  fontWeight: 600,
  fontSize: '14px',
  textDecoration: 'none',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateX(5px)',
    boxShadow: `0px 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
  }
}));

const FloatingIcon = styled(Box)(({ theme }) => ({
  position: 'absolute',
  color: alpha(theme.palette.primary.main, 0.3),
  animation: 'float 6s ease-in-out infinite',
  '@keyframes float': {
    '0%': { transform: 'translateY(0px)' },
    '50%': { transform: 'translateY(-20px)' },
    '100%': { transform: 'translateY(0px)' },
  }
}));

const ComingSoon = ({ configData, landingPageData }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const isXSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    const updateCountdown = () => {
      const timeData = getTimeUntilLaunch();
      setTimeLeft(timeData);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, []);

  const timeUnits = [
    { label: 'Days', value: timeLeft.days, icon: CalendarTodayIcon },
    { label: 'Hours', value: timeLeft.hours, icon: AccessTimeIcon },
    { label: 'Minutes', value: timeLeft.minutes, icon: AccessTimeIcon },
    { label: 'Seconds', value: timeLeft.seconds, icon: AccessTimeIcon }
  ];

  if (!mounted) {
    return null; // Prevent hydration mismatch
  }

  return (
    <ComingSoonContainer>
      {/* Floating Background Icons */}
      <FloatingIcon sx={{ top: '10%', left: '10%', animationDelay: '0s' }}>
        <StarIcon sx={{ fontSize: 40 }} />
      </FloatingIcon>
      <FloatingIcon sx={{ top: '20%', right: '15%', animationDelay: '2s' }}>
        <RocketLaunchIcon sx={{ fontSize: 35 }} />
      </FloatingIcon>
      <FloatingIcon sx={{ bottom: '15%', left: '20%', animationDelay: '4s' }}>
        <StarIcon sx={{ fontSize: 30 }} />
      </FloatingIcon>
      <FloatingIcon sx={{ bottom: '25%', right: '10%', animationDelay: '1s' }}>
        <StarIcon sx={{ fontSize: 25 }} />
      </FloatingIcon>

      <Container maxWidth="lg">
        <Fade in={mounted} timeout={1000}>
          <MainCard elevation={0}>
            <CustomStackFullWidth spacing={4} alignItems="center">
              
              {/* Logo Section */}
              <Zoom in={mounted} timeout={1200}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.05)' },
                      '100%': { transform: 'scale(1)' },
                    }
                  }}
                >
                  {configData?.logo ? (
                    <Box
                      sx={{
                        width: { xs: '120px', md: '150px' },
                        height: { xs: '120px', md: '150px' },
                        borderRadius: '50%',
                        backgroundColor: theme.palette.whiteContainer.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: { xs: 2.5, md: 3 },
                        boxShadow: `0px 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CustomImageContainer
                          src={`${configData?.base_urls?.business_logo_url}/${configData?.logo}`}
                          alt={configData?.business_name || 'Logo'}
                          height="auto"
                          width="auto"
                          objectFit="contain"
                          sx={{
                            maxWidth: '90%',
                            maxHeight: '90%',
                            width: 'auto',
                            height: 'auto'
                          }}
                        />
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: { xs: 80, md: 120 },
                        height: { xs: 80, md: 120 },
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.customType2})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0px 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`
                      }}
                    >
                      <RocketLaunchIcon 
                        sx={{ 
                          fontSize: { xs: 40, md: 60 }, 
                          color: theme.palette.whiteContainer.main 
                        }} 
                      />
                    </Box>
                  )}
                </Box>
              </Zoom>

              {/* Main Heading */}
              <CustomStackFullWidth spacing={2} alignItems="center">
                <Typography
                  variant={isXSmall ? "h3" : "h1"}
                  sx={{
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.customType2})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textAlign: 'center',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Coming Soon
                </Typography>
                
                <Typography
                  variant={isXSmall ? "h5" : "h3"}
                  sx={{
                    color: theme.palette.text.primary,
                    textAlign: 'center',
                    fontWeight: 600,
                    maxWidth: '600px'
                  }}
                >
                  {configData?.business_name ? `${configData.business_name} is Coming Soon!` : 'Something Amazing is on the Way!'}
                </Typography>
                
                <Typography
                  variant={isXSmall ? "body1" : "h6"}
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: 'center',
                    maxWidth: '500px',
                    lineHeight: 1.6
                  }}
                >
                  We're putting the finishing touches on our new shopping experience. 
                  Get ready for something extraordinary!
                </Typography>
              </CustomStackFullWidth>

              {/* Countdown Timer */}
              <CustomStackFullWidth spacing={3} alignItems="center">
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    textAlign: 'center'
                  }}
                >
                  Launch Date: {getFormattedLaunchDate()}
                </Typography>
                
                <Grid container spacing={2} justifyContent="center" maxWidth="600px">
                  {timeUnits.map((unit, index) => {
                    const IconComponent = unit.icon;
                    return (
                      <Grid item xs={6} sm={3} key={unit.label}>
                        <Zoom in={mounted} timeout={800 + (index * 200)}>
                          <CountdownCard elevation={0}>
                            <Stack spacing={1} alignItems="center">
                              <IconComponent 
                                sx={{ 
                                  fontSize: 24, 
                                  color: theme.palette.primary.main 
                                }} 
                              />
                              <Typography
                                variant={isXSmall ? "h4" : "h3"}
                                sx={{
                                  fontWeight: 800,
                                  color: theme.palette.primary.main,
                                  lineHeight: 1
                                }}
                              >
                                {unit.value.toString().padStart(2, '0')}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontWeight: 500,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}
                              >
                                {unit.label}
                              </Typography>
                            </Stack>
                          </CountdownCard>
                        </Zoom>
                      </Grid>
                    );
                  })}
                </Grid>
              </CustomStackFullWidth>

              {/* Join Us Section - Show if we have earning data or fallback to default */}
              {(landingPageData?.earning_seller_title || landingPageData?.earning_dm_title || !landingPageData) && (
                <CustomStackFullWidth spacing={3} alignItems="center">
                  <Typography
                    variant={isXSmall ? "h5" : "h4"}
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 700,
                      textAlign: 'center'
                    }}
                  >
                    {landingPageData?.earning_title ? (
                      <DollarSignHighlighter
                        theme={theme}
                        text={landingPageData.earning_title}
                      />
                    ) : (
                      'Join Our Platform Early!'
                    )}
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      textAlign: 'center',
                      maxWidth: '600px',
                      lineHeight: 1.6
                    }}
                  >
                    {landingPageData?.earning_sub_title ? (
                      <DollarSignHighlighter
                        theme={theme}
                        text={landingPageData.earning_sub_title}
                      />
                    ) : (
                      'Don\'t wait for launch day! Smart store owners are already joining our platform. Register now and be ready to start selling from day one!'
                    )}
                  </Typography>

                  {/* CTA Cards */}
                  <Grid container spacing={3} maxWidth="700px" justifyContent="center">
                    {(landingPageData?.earning_seller_title || !landingPageData) && (
                      <Grid item xs={12} md={(landingPageData?.earning_dm_title || !landingPageData) ? 6 : 12}>
                        <Zoom in={mounted} timeout={1400}>
                          <CTACard 
                            elevation={0}
                            onClick={() => window.open(landingPageData?.earning_seller_button_url || `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/store/apply`, '_blank')}
                          >
                            <Stack spacing={2} alignItems="center" textAlign="center">
                              <Box
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: '50%',
                                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.customType2})`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 1
                                }}
                              >
                                {landingPageData?.base_urls?.earning_image_url ? (
                                  <CustomImageContainer
                                    src={`${landingPageData.base_urls.earning_image_url}/seller.svg`}
                                    alt="Seller"
                                    height="50px"
                                    width="50px"
                                    objectFit="contain"
                                  />
                                ) : (
                                  <StoreIcon sx={{ fontSize: 40, color: theme.palette.whiteContainer.main }} />
                                )}
                              </Box>
                              
                              <Typography
                                variant="h6"
                                sx={{
                                  color: theme.palette.text.primary,
                                  fontWeight: 600
                                }}
                              >
                                {landingPageData?.earning_seller_title ? (
                                  <DollarSignHighlighter
                                    theme={theme}
                                    text={landingPageData.earning_seller_title}
                                  />
                                ) : (
                                  'Become a Store Owner'
                                )}
                              </Typography>
                              
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  lineHeight: 1.5
                                }}
                              >
                                {landingPageData?.earning_seller_sub_title ? (
                                  <DollarSignHighlighter
                                    theme={theme}
                                    text={landingPageData.earning_seller_sub_title}
                                  />
                                ) : (
                                  'Get ahead of the competition! Early store partners enjoy priority placement, reduced fees, and exclusive marketing support.'
                                )}
                              </Typography>
                              
                              <CTAButton component="span">
                                {landingPageData?.earning_seller_button_name ? (
                                  <DollarSignHighlighter
                                    theme={theme}
                                    text={landingPageData.earning_seller_button_name}
                                  />
                                ) : (
                                  'Apply Now'
                                )}
                                <ArrowForwardIcon sx={{ fontSize: 16 }} />
                              </CTAButton>
                            </Stack>
                          </CTACard>
                        </Zoom>
                      </Grid>
                    )}
                    
                    {(landingPageData?.earning_dm_title || !landingPageData) && (
                      <Grid item xs={12} md={(landingPageData?.earning_seller_title || !landingPageData) ? 6 : 12}>
                        <Zoom in={mounted} timeout={1600}>
                          <CTACard 
                            elevation={0}
                            onClick={() => window.open(landingPageData?.earning_dm_button_url || `${process.env.REACT_APP_BASE_URL || 'https://admin.alot263.com'}/deliveryman/apply`, '_blank')}
                          >
                            <Stack spacing={2} alignItems="center" textAlign="center">
                              <Box
                                sx={{
                                  width: 80,
                                  height: 80,
                                  borderRadius: '50%',
                                  background: `linear-gradient(135deg, ${theme.palette.primary.customType2}, ${theme.palette.primary.customType3})`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 1
                                }}
                              >
                                {landingPageData?.base_urls?.earning_image_url ? (
                                  <CustomImageContainer
                                    src={`${landingPageData.base_urls.earning_image_url}/delivery-man.svg`}
                                    alt="Delivery Man"
                                    height="50px"
                                    width="50px"
                                    objectFit="contain"
                                  />
                                ) : (
                                  <DeliveryDiningIcon sx={{ fontSize: 40, color: theme.palette.whiteContainer.main }} />
                                )}
                              </Box>
                              
                              <Typography
                                variant="h6"
                                sx={{
                                  color: theme.palette.text.primary,
                                  fontWeight: 600
                                }}
                              >
                                {landingPageData?.earning_dm_title ? (
                                  <DollarSignHighlighter
                                    theme={theme}
                                    text={landingPageData.earning_dm_title}
                                  />
                                ) : (
                                  'Become a Delivery Partner'
                                )}
                              </Typography>
                              
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  lineHeight: 1.5
                                }}
                              >
                                {landingPageData?.earning_dm_sub_title ? (
                                  <DollarSignHighlighter
                                    theme={theme}
                                    text={landingPageData.earning_dm_sub_title}
                                  />
                                ) : (
                                  'Earn money on your schedule. Join our delivery network and start earning today.'
                                )}
                              </Typography>
                              
                              <CTAButton component="span">
                                {landingPageData?.earning_dm_button_name ? (
                                  <DollarSignHighlighter
                                    theme={theme}
                                    text={landingPageData.earning_dm_button_name}
                                  />
                                ) : (
                                  'Join Now'
                                )}
                                <ArrowForwardIcon sx={{ fontSize: 16 }} />
                              </CTAButton>
                            </Stack>
                          </CTACard>
                        </Zoom>
                      </Grid>
                    )}
                  </Grid>
                </CustomStackFullWidth>
              )}

              {/* Enhanced Features Section */}
              <CustomStackFullWidth spacing={4} alignItems="center" sx={{ maxWidth: '800px' }}>
                <Typography
                  variant={isXSmall ? "h5" : "h4"}
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    textAlign: 'center',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.customType2})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  What's Coming Your Way
                </Typography>

                <Grid container spacing={3} justifyContent="center">
                  {[
                    {
                      icon: SpeedIcon,
                      title: 'Lightning Fast',
                      description: 'Optimized for speed with instant loading and seamless navigation',
                      color: theme.palette.primary.main
                    },
                    {
                      icon: SecurityIcon,
                      title: 'Secure & Safe',
                      description: 'Bank-level security to protect your data and transactions',
                      color: theme.palette.primary.customType2
                    },
                    {
                      icon: TrendingUpIcon,
                      title: 'Grow Sales Fast',
                      description: 'Reach thousands of customers instantly with our powerful marketing tools',
                      color: theme.palette.primary.customType3
                    },
                    {
                      icon: NotificationsActiveIcon,
                      title: 'Real-Time Orders',
                      description: 'Instant notifications for new orders and seamless order management',
                      color: theme.palette.primary.main
                    }
                  ].map((feature, index) => {
                    const IconComponent = feature.icon;
                    return (
                      <Grid item xs={12} sm={6} md={3} key={index}>
                        <Zoom in={mounted} timeout={1000 + (index * 200)}>
                          <Box
                            sx={{
                              textAlign: 'center',
                              padding: 3,
                              borderRadius: '20px',
                              background: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.paper, 0.9)
                                : theme.palette.background.paper,
                              border: `1px solid ${alpha(feature.color, 0.2)}`,
                              boxShadow: theme.palette.mode === 'dark'
                                ? `0px 8px 32px ${alpha(theme.palette.common.black, 0.3)}`
                                : `0px 8px 32px ${alpha(feature.color, 0.1)}`,
                              transition: 'all 0.3s ease-in-out',
                              '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: theme.palette.mode === 'dark'
                                  ? `0px 12px 40px ${alpha(theme.palette.common.black, 0.4)}`
                                  : `0px 12px 40px ${alpha(feature.color, 0.2)}`,
                                borderColor: feature.color,
                              }
                            }}
                          >
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${feature.color}, ${alpha(feature.color, 0.7)})`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px auto',
                                boxShadow: `0px 4px 16px ${alpha(feature.color, 0.3)}`
                              }}
                            >
                              <IconComponent 
                                sx={{ 
                                  fontSize: 28, 
                                  color: theme.palette.whiteContainer.main 
                                }} 
                              />
                            </Box>
                            
                            <Typography
                              variant="h6"
                              sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                                marginBottom: 1
                              }}
                            >
                              {feature.title}
                            </Typography>
                            
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.5
                              }}
                            >
                              {feature.description}
                            </Typography>
                          </Box>
                        </Zoom>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Call to Action Banner */}
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '600px',
                    padding: 4,
                    borderRadius: '24px',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.customType2, 0.1)} 100%)`,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `radial-gradient(circle at 30% 70%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%)`,
                      zIndex: 1,
                    }
                  }}
                >
                  <Stack spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          animation: 'pulse 2s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1, transform: 'scale(1)' },
                            '50%': { opacity: 0.5, transform: 'scale(1.2)' },
                            '100%': { opacity: 1, transform: 'scale(1)' },
                          }
                        }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          fontSize: '14px'
                        }}
                      >
                        Coming Soon
                      </Typography>
                    </Box>
                    
                    <Typography
                      variant={isXSmall ? "h6" : "h5"}
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                        textAlign: 'center'
                      }}
                    >
                      Early Store Partners Get Exclusive Benefits
                    </Typography>
                    
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.text.secondary,
                        textAlign: 'center',
                        lineHeight: 1.6
                      }}
                    >
                      Join hundreds of forward-thinking store owners who are already setting up their businesses. 
                      Early partners enjoy reduced commission rates, priority customer placement, and dedicated onboarding support!
                    </Typography>
                  </Stack>
                </Box>
              </CustomStackFullWidth>

            </CustomStackFullWidth>
          </MainCard>
        </Fade>
      </Container>
    </ComingSoonContainer>
  );
};

export default ComingSoon;
